import classNames from 'classnames';
import React, { useCallback, useRef, useState } from 'react';
import { Button as BButton } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

/**
 * @typedef ButtonParams
 * @property {('primary'|'secondary'|'tertiary')} variant
 * @property {('sm'|'lg'|'xs')} size
 */

/**
 *
 * @param {ButtonParams} params
 * @returns
 */
const Button = ({
  onClick,
  onFocus,
  onBlur,
  className,
  children,
  variant,
  disabled,
  id,
  idButton,
  lastStep,
  to,
  size = 'lg',
  containerStyle,
  icon,
  ...params
}) => {
  const button = useRef();
  const [focused, setFocused] = useState(className && className.includes('focus'));

  const handleClick = useCallback(() => {
    button.current.blur();
    if (onClick) {
      onClick();
    }
  }, [onClick]);

  const handleFocus = useCallback(() => {
    setFocused(true);
    if (onFocus) {
      onFocus();
    }
  }, [onFocus]);

  const handleBlur = useCallback(() => {
    setFocused(false);
    if (onBlur) {
      onBlur();
    }
  }, [onBlur]);

  const handleMouseLeave = useCallback(() => {
    setTimeout(() => {
      setFocused(false);
    }, 0);
  }, []);

  const renderChildren = useCallback((children, isTertiary) => {
    if (!isTertiary) return children;
    if (!Array.isArray(children)) {
      return children;
    }
    return children.map((child, index) => (
      typeof child === 'string' ? <span key={index}>{child}</span> : child
    ));
  }, []);

  const buttonRender = <BButton
    id={idButton}
    data-last-step={String(lastStep)}
    variant={variant}
    className={classNames(className)}
    disabled={disabled}
    to={to}
    {...params}
  >
    {renderChildren(children, variant === 'tertiary')}
  </BButton>;

  return (
    <span
      ref={button}
      className={classNames('btn-gradient', {
        focus: focused || className && className.includes('focus'),
        disabled,
        hover: className && className.includes('hover'),
        small: size === 'sm',
        xs: size === 'xs',
        noMinWidth: (className && className.includes('no-min-width') || icon),
        icon
      })}
      id={id}
      onClick={to ? null : handleClick}
      onFocus={handleFocus}
      onBlur={handleBlur}
      onMouseLeave={handleMouseLeave}
      style={containerStyle}
    >
      {
        ['primary', 'secondary'].includes(variant) &&
        <div className="background-gradient" />
      }
      {buttonRender}
    </span >
  );
};

Button.propTypes = {
  onClick: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  className: PropTypes.any,
  children: PropTypes.any,
  variant: PropTypes.oneOf(['primary', 'secondary', 'tertiary', 'link']),
  disabled: PropTypes.bool,
  id: PropTypes.string,
  idButton: PropTypes.string,
  lastStep: PropTypes.bool,
  to: PropTypes.string,
  size: PropTypes.string,
  containerStyle: PropTypes.object,
  icon: PropTypes.bool
};

Button.defaultProps = {
  onClick: () => { },
  onFocus: () => { },
  onBlur: () => { },
  className: null,
  children: null,
  variant: 'tertiary',
  disabled: false,
  id: null,
  idButton: null,
  lastStep: null,
  to: null,
  size: 'lg',
  containerStyle: {},
  icon: false
};

export default React.memo(Button);
